import { supabase } from './client'
import { fileToProfileImageBase64 } from '@lib/constants/profileImageUpload'
import { memCache, TTL } from '@lib/cache'

// ---- Types ------------------------------------------------------------------
export interface PatientProfile {
  id: string
  auth_id: string
  full_name: string | null
  email: string | null
  phone_number: string | null
  date_of_birth: string | null
  gender: string | null
  blood_type: string | null
  height_cm: number | null
  weight_kg: number | null
  profile_image_url: string | null
  verification_status: string | null
  created_at: string
}

type PatientProfileRow = {
  id: string
  auth_id: string
  name: string | null
  email?: string | null
  phone_number: string | null
  date_of_birth: string | null
  gender: string | null
  blood_type: string | null
  height: string | number | null
  weight: string | number | null
  profile_image_url: string | null
  verification_status: string | null
  created_at: string
}

function toNullableNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function mapPatientProfile(row: PatientProfileRow | null): PatientProfile | null {
  if (!row) return null
  return {
    id: row.id,
    auth_id: row.auth_id,
    full_name: row.name ?? null,
    email: row.email ?? null,
    phone_number: row.phone_number ?? null,
    date_of_birth: row.date_of_birth ?? null,
    gender: row.gender ?? null,
    blood_type: row.blood_type ?? null,
    height_cm: toNullableNumber(row.height),
    weight_kg: toNullableNumber(row.weight),
    profile_image_url: row.profile_image_url ?? null,
    verification_status: row.verification_status ?? null,
    created_at: row.created_at,
  }
}

function mapPatientProfileUpdates(updates: Partial<PatientProfile>) {
  const mapped: Record<string, unknown> = {}

  if ('full_name' in updates) mapped.name = updates.full_name ?? null
  if ('email' in updates) mapped.email = updates.email ?? null
  if ('phone_number' in updates) mapped.phone_number = updates.phone_number ?? null
  if ('date_of_birth' in updates) mapped.date_of_birth = updates.date_of_birth ?? null
  if ('gender' in updates) mapped.gender = updates.gender ?? null
  if ('blood_type' in updates) mapped.blood_type = updates.blood_type ?? null
  if ('height_cm' in updates) mapped.height = updates.height_cm != null ? String(updates.height_cm) : null
  if ('weight_kg' in updates) mapped.weight = updates.weight_kg != null ? String(updates.weight_kg) : null
  if ('profile_image_url' in updates) mapped.profile_image_url = updates.profile_image_url ?? null
  if ('verification_status' in updates) mapped.verification_status = updates.verification_status ?? null

  return mapped
}

export interface EmergencyContact {
  id: string
  patient_id: string
  name: string
  relationship: string
  phone_number: string
  is_primary: boolean
  created_at: string
}


export interface PatientAddress {
  id: string
  patient_id: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  country: string
  is_default: boolean
  label: string | null
  latitude?: number | null
  longitude?: number | null
  created_at: string
}

type PatientAddressRow = {
  id: string
  patient_id: string
  title: string | null
  address_line_1: string
  city: string
  state: string
  country: string
  landmark: string | null
  is_default: boolean | null
  latitude?: number | null
  longitude?: number | null
  created_at: string
}

type PatientAddressInput = Omit<PatientAddress, 'id' | 'patient_id' | 'created_at'>

function mapPatientAddress(row: PatientAddressRow): PatientAddress {
  return {
    id: row.id,
    patient_id: row.patient_id,
    label: row.title ?? null,
    address_line1: row.address_line_1,
    address_line2: row.landmark ?? null,
    city: row.city,
    state: row.state,
    country: row.country,
    is_default: Boolean(row.is_default),
    latitude: row.latitude ?? null,
    longitude: row.longitude ?? null,
    created_at: row.created_at,
  }
}

function mapPatientAddressInput(address: Partial<PatientAddressInput>) {
  const mapped: Record<string, unknown> = {}

  if ('label' in address) mapped.title = address.label ?? null
  if ('address_line1' in address) mapped.address_line_1 = address.address_line1 ?? ''
  if ('address_line2' in address) mapped.landmark = address.address_line2 ?? null
  if ('city' in address) mapped.city = address.city ?? ''
  if ('state' in address) mapped.state = address.state ?? ''
  if ('country' in address) mapped.country = address.country ?? 'Nigeria'
  if ('is_default' in address) mapped.is_default = address.is_default ?? false
  if ('latitude' in address) mapped.latitude = address.latitude ?? null
  if ('longitude' in address) mapped.longitude = address.longitude ?? null

  return mapped
}

export interface Transaction {
  id: string
  patient_id: string
  type: string
  amount: number
  description: string | null
  reference: string | null
  status: string
  created_at: string
}

export interface PaystackInitializeResult {
  success: boolean
  reference?: string
  authorizationUrl?: string
  amountNaira?: number
  error?: string
}

export interface PaymentIntentStatus {
  status: 'initialized' | 'pending' | 'success' | 'failed' | 'abandoned' | 'expired' | 'not_found'
  reference: string
}

// ---- Helpers ----------------------------------------------------------------
async function getPatientId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const cacheKey = `patient_id:${user.id}`
  const cached = memCache.get<string>(cacheKey)
  if (cached) return cached

  const { data } = await supabase.from('patients').select('id').eq('auth_id', user.id).maybeSingle()
  if (!data?.id) return null

  memCache.set(cacheKey, data.id, TTL.ACTOR_ID)
  return data.id
}

// ---- Service ----------------------------------------------------------------
export const patientProfileService = {
  async getWalletBalance(): Promise<number> {
    try {
      const pid = await getPatientId()
      if (!pid) return 0
      const { data, error } = await supabase
        .from('patient_wallet').select('balance').eq('patient_id', pid).maybeSingle()
      if (error) throw error
      return data?.balance ?? 0
    } catch {
      return 0
    }
  },

  async getProfile(): Promise<PatientProfile | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null
      const { data, error } = await supabase
        .from('patients').select('*').eq('auth_id', user.id).maybeSingle()
      if (error) throw error
      if (!data) return null

      return mapPatientProfile({
        ...(data as PatientProfileRow),
        email: (data as PatientProfileRow).email ?? user.email ?? null,
      })
    } catch {
      return null
    }
  },

  async updateProfile(updates: Partial<PatientProfile>): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return { success: false, error: 'Not authenticated' }
      const payload = mapPatientProfileUpdates(updates)
      const { error } = await supabase.from('patients').update(payload).eq('auth_id', user.id)
      if (error) throw error
      return { success: true }
    } catch (e: any) {
      return { success: false, error: e.message }
    }
  },

  async uploadProfileImage(file: File): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return { success: false, error: 'Not authenticated' }

      const { base64, mime } = await fileToProfileImageBase64(file)
      const { data, error } = await supabase.functions.invoke('upload-profile-image', {
        body: {
          role: 'patient',
          contentType: mime,
          imageBase64: base64,
        },
      })

      if (error) return { success: false, error: error.message }
      if (!data?.success) return { success: false, error: data?.error ?? 'Failed to upload profile image.' }

      const url = data.url || data.imageUrl
      if (!url) return { success: false, error: 'Failed to generate public URL.' }

      return { success: true, url }
    } catch (e: any) {
      return { success: false, error: e.message }
    }
  },

  async getTransactions(page = 0, pageSize = 10): Promise<Transaction[]> {
    try {
      const pid = await getPatientId()
      if (!pid) return []
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('patient_id', pid)
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1)
      if (error) throw error
      return (data ?? []) as Transaction[]
    } catch {
      return []
    }
  },

  async initializePaystackTopup(amountNaira: number): Promise<PaystackInitializeResult> {
    try {
      const { data, error } = await supabase.functions.invoke('initialize-paystack-topup', {
        body: { amountNaira, channels: ['card', 'bank', 'ussd', 'bank_transfer'] },
      })
      if (error) throw new Error(error.message)
      if (!data?.success || !data?.authorizationUrl) {
        return { success: false, error: data?.error ?? 'Invalid payment response' }
      }
      return {
        success: true,
        reference: data.reference,
        authorizationUrl: data.authorizationUrl,
        amountNaira: data.amountNaira,
      }
    } catch (e: any) {
      return { success: false, error: e.message }
    }
  },

  async getPaymentIntentStatus(reference: string): Promise<PaymentIntentStatus> {
    try {
      const { data, error } = await supabase
        .from('payment_intents')
        .select('status, reference')
        .eq('reference', reference)
        .maybeSingle()
      if (error || !data?.status) return { status: 'not_found', reference }
      const allowed: PaymentIntentStatus['status'][] = [
        'initialized', 'pending', 'success', 'failed', 'abandoned', 'expired',
      ]
      return {
        status: allowed.includes(data.status as any) ? (data.status as PaymentIntentStatus['status']) : 'not_found',
        reference: data.reference ?? reference,
      }
    } catch {
      return { status: 'not_found', reference }
    }
  },

  // ── Addresses ─────────────────────────────────────────────────────────────

  async getAddresses(): Promise<PatientAddress[]> {
    const patientId = await getPatientId()
    if (!patientId) return []
    const { data, error } = await supabase
      .from('patient_addresses')
      .select('*')
      .eq('patient_id', patientId)
      .order('is_default', { ascending: false })
    if (error) return []
    return ((data ?? []) as PatientAddressRow[]).map(mapPatientAddress)
  },

  async addAddress(address: PatientAddressInput): Promise<{ success: boolean; error?: string }> {
    const patientId = await getPatientId()
    if (!patientId) return { success: false, error: 'Not authenticated' }
    if (address.is_default) {
      await supabase.from('patient_addresses').update({ is_default: false }).eq('patient_id', patientId)
    }
    const payload = mapPatientAddressInput(address)
    const { error } = await supabase.from('patient_addresses').insert({ ...payload, patient_id: patientId })
    return error ? { success: false, error: error.message } : { success: true }
  },

  async updateAddress(id: string, updates: Partial<PatientAddressInput>): Promise<{ success: boolean; error?: string }> {
    const patientId = await getPatientId()
    if (!patientId) return { success: false, error: 'Not authenticated' }
    if (updates.is_default) {
      await supabase.from('patient_addresses').update({ is_default: false }).eq('patient_id', patientId)
    }
    const payload = mapPatientAddressInput(updates)
    const { error } = await supabase.from('patient_addresses').update(payload).eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  async deleteAddress(id: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('patient_addresses').delete().eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  async setDefaultAddress(id: string): Promise<{ success: boolean; error?: string }> {
    const patientId = await getPatientId()
    if (!patientId) return { success: false, error: 'Not authenticated' }
    await supabase.from('patient_addresses').update({ is_default: false }).eq('patient_id', patientId)
    const { error } = await supabase.from('patient_addresses').update({ is_default: true }).eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Emergency Contacts ────────────────────────────────────────────────────

  async getEmergencyContacts(): Promise<EmergencyContact[]> {
    const patientId = await getPatientId()
    if (!patientId) return []
    const { data } = await supabase
      .from('emergency_contacts')
      .select('*')
      .eq('patient_id', patientId)
      .order('is_primary', { ascending: false })
    return (data ?? []) as EmergencyContact[]
  },

  async addEmergencyContact(contact: Omit<EmergencyContact, 'id' | 'patient_id' | 'created_at'>): Promise<{ success: boolean; error?: string }> {
    const patientId = await getPatientId()
    if (!patientId) return { success: false, error: 'Not authenticated' }
    const { error } = await supabase.from('emergency_contacts').insert({ ...contact, patient_id: patientId })
    return error ? { success: false, error: error.message } : { success: true }
  },

  async updateEmergencyContact(id: string, updates: Partial<Omit<EmergencyContact, 'id' | 'patient_id' | 'created_at'>>): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('emergency_contacts').update(updates).eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  async deleteEmergencyContact(id: string): Promise<{ success: boolean; error?: string }> {
    const { error } = await supabase.from('emergency_contacts').delete().eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  async setPrimaryEmergencyContact(id: string): Promise<{ success: boolean; error?: string }> {
    const patientId = await getPatientId()
    if (!patientId) return { success: false, error: 'Not authenticated' }
    await supabase.from('emergency_contacts').update({ is_primary: false }).eq('patient_id', patientId)
    const { error } = await supabase.from('emergency_contacts').update({ is_primary: true }).eq('id', id)
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Security ──────────────────────────────────────────────────────────────

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    // Supabase requires re-authentication — sign in again with current password to verify
    const { data: { user } } = await supabase.auth.getUser()
    if (!user?.email) return { success: false, error: 'Unable to get current user' }
    const { error: signInError } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword })
    if (signInError) return { success: false, error: 'Current password is incorrect' }
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    return error ? { success: false, error: error.message } : { success: true }
  },

  // ── Static content ────────────────────────────────────────────────────────

  async getStaticDocument(title: string): Promise<{ title: string; content: string; updatedAt?: string } | null> {
    const { data } = await supabase
      .from('static_content')
      .select('title, content, updated_at')
      .eq('type', 'document')
      .eq('title', title)
      .order('updated_at', { ascending: false })
      .limit(1)
    const row = (data ?? [])[0]
    if (!row) return null
    return { title: (row as any).title, content: (row as any).content ?? '', updatedAt: (row as any).updated_at }
  },

  async getPatientTerms() { return this.getStaticDocument('Patient Terms of Service') },
  async getPatientPrivacyPolicy() { return this.getStaticDocument('Patient Privacy Policy') },
  async getPatientDataUsagePolicy() { return this.getStaticDocument('Patient Data Usage Policy') },

  async getPatientFAQs(): Promise<{ id: string; title: string; content: string }[]> {
    const { data } = await supabase
      .from('static_content')
      .select('id, title, content')
      .eq('type', 'faq')
      .in('category', ['patient', 'general'])
      .order('order_index', { ascending: true })
    return (data ?? []) as { id: string; title: string; content: string }[]
  },

  async getPatientAppConfig(): Promise<{ supportPhone: string; supportWhatsApp: string; supportEmail: string } | null> {
    const { data } = await supabase.from('app_config').select('support_phone, support_whatsapp, support_email').limit(1).maybeSingle()
    if (!data) return null
    return { supportPhone: (data as any).support_phone ?? '', supportWhatsApp: (data as any).support_whatsapp ?? '', supportEmail: (data as any).support_email ?? '' }
  },

  async submitPatientFeedback(params: { subject: string; message: string; rating?: number }): Promise<{ success: boolean; error?: string }> {
    const patientId = await getPatientId()
    if (!patientId) return { success: false, error: 'Not authenticated' }
    const { error } = await supabase.from('support_feedback').insert({
      patient_id: patientId,
      subject: params.subject || 'Patient Feedback',
      message: params.message,
      rating: params.rating ?? null,
      type: 'patient',
    })
    return error ? { success: false, error: error.message } : { success: true }
  },
}
